import React, { useState, useEffect } from 'react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import QuantityInput from '../../components/QuantityInput/QuantityInput';
import ConfirmationModel from '../../components/confirmationModel/ConfirmationModel';
import { FaImage, FaRegTrashAlt } from 'react-icons/fa';
import { formatRupiah } from '../../utils/Formatter';
import './CartPage.css';

const CartPage = () => {
    const { cartItems, updateQuantity, removeFromCart, clearCart } = useCart();
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [userProfile, setUserProfile] = useState(null);
    const [isLoadingProfile, setIsLoadingProfile] = useState(true);
    const [deliveryMethod, setDeliveryMethod] = useState('diantar');
    const [guestInfo, setGuestInfo] = useState({ customerName: '', customerAddress: '', customerPhone: '' });
    const [useOptionalAddress, setUseOptionalAddress] = useState(false);
    const [optionalInfo, setOptionalInfo] = useState({ optionalAddress: '', optionalPhone: '' });
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const API_URL = api.defaults.baseURL.replace('/api', '');

    useEffect(() => {
        if (isAuthenticated) {
            setIsLoadingProfile(true);
            api.get('/users/me')
                .then(res => setUserProfile(res.data))
                .catch(() => {
                    toast.error("Gagal memuat data profil Anda.");
                    setUserProfile(null);
                })
                .finally(() => setIsLoadingProfile(false));
        } else {
            setIsLoadingProfile(false);
            setUserProfile(null);
        }
    }, [isAuthenticated]);

    const handleGuestInfoChange = (e) => setGuestInfo(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleDeliveryMethodChange = (e) => {
        const newMethod = e.target.value;
        setDeliveryMethod(newMethod);
        if (newMethod === 'dijemput') {
            setUseOptionalAddress(false);
        }
    };

    const handleOptionalInfoChange = (e) => setOptionalInfo({ ...optionalInfo, [e.target.name]: e.target.value });

    const handleProceedToCheckout = (e) => {
        e.preventDefault();
        if (cartItems.some(item => !item.Product)) {
            toast.error("Keranjang berisi produk yang tidak valid. Harap hapus terlebih dahulu.");
            return;
        }

        if (isAuthenticated) {
            if (!userProfile) {
                toast.error("Data profil tidak dapat dimuat. Coba lagi.");
                return;
            }
            if (deliveryMethod === 'diantar') {
                const isPrimaryAddressEmpty = !userProfile.address || !userProfile.phone_number;
                if (!useOptionalAddress && isPrimaryAddressEmpty) {
                    toast.warn("Alamat utama Anda kosong. Harap lengkapi di profil atau pilih 'Kirim ke alamat lain'.");
                    navigate('/profile');
                    return;
                }
                if (useOptionalAddress && (!optionalInfo.optionalAddress.trim() || !optionalInfo.optionalPhone.trim())) {
                    toast.warn("Harap isi Alamat dan Telepon tujuan.");
                    return;
                }
            }
        } else {
            if (!guestInfo.customerName.trim()) {
                toast.warn("Harap isi Nama Pemesan.");
                return;
            }
            if (deliveryMethod === 'diantar' && !guestInfo.customerAddress.trim()) {
                toast.warn("Harap isi Alamat Pengiriman.");
                return;
            }
        }
        setIsConfirmModalOpen(true);
    };

    const handleConfirmCheckout = async () => {
        setIsConfirmModalOpen(false);
        const toastId = toast.loading("Memproses pesanan Anda...");
        
        const validItems = cartItems.filter(item => item.Product);
        if (validItems.length === 0) {
            toast.update(toastId, { render: "Tidak ada produk valid di keranjang.", type: "error", isLoading: false, autoClose: 3000 });
            return;
        }
    
        try {
            const payload = { deliveryMethod };
            let response;
    
            if (isAuthenticated) {
                payload.optionalAddress = useOptionalAddress ? optionalInfo.optionalAddress : undefined;
                payload.optionalPhone = useOptionalAddress ? optionalInfo.optionalPhone : undefined;
                response = await api.post('/orders/from-cart', payload);
            } else {
                payload.guestInfo = { ...guestInfo, deliveryMethod };
                payload.items = validItems.map(item => ({
                    productId: item.Product.id,
                    quantity: item.quantity
                }));
                response = await api.post('/orders/guest', payload); 
            }
    
            const createdOrder = response.data.order;
            toast.update(toastId, { render: "Pesanan berhasil dibuat!", type: "success", isLoading: false, autoClose: 2000 });
            
            const whatsAppNumber = '6285133367953';
            let message = `*Konfirmasi Pesanan Baru*\n\n` +
                          `*ID Pesanan:* ${createdOrder.id.substring(0, 8)}\n` +
                          `*Nama:* ${createdOrder.customerName}\n` +
                          (createdOrder.customerPhone ? `*Telepon:* ${createdOrder.customerPhone}\n` : '') +
                          `*Alamat:* ${createdOrder.customerAddress}\n\n` +
                          `*Item Dipesan:*\n` +
                          validItems.map(item => `- ${item.Product.Barang.nama_barang} (${item.Product.nama_satuan}) (x${item.quantity})`).join('\n') +
                          `\n\n*Total Pembayaran:* ${formatRupiah(createdOrder.totalAmount)}\n` +
                          `*Metode Pengiriman:* ${createdOrder.deliveryMethod === 'diantar' ? 'Diantar' : 'Dijemput'}\n\n` +
                          `Mohon info lanjut untuk pembayaran. Terima kasih.`;
            
            const whatsappUrl = `https://api.whatsapp.com/send/?phone=${whatsAppNumber}&text=${encodeURIComponent(message)}`;
            
            clearCart();
            window.open(whatsappUrl, '_blank');
            navigate('/');
    
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Gagal membuat pesanan.";
            toast.update(toastId, { render: errorMessage, type: "error", isLoading: false, autoClose: 3000 });
        }
    };
    
    const handleQuantityChange = (item, newQuantity) => {
        updateQuantity(item.Product.id, newQuantity);
    };

    const total = cartItems
        .filter(item => item.Product)
        .reduce((sum, item) => sum + item.quantity * (parseFloat(item.Product.price) || 0), 0);

    const renderAuthenticatedUserForm = () => {
        if (isLoadingProfile) return <p>Memuat profil...</p>;
        if (!userProfile) return <p>Gagal memuat profil. Silakan coba lagi.</p>;

        return deliveryMethod === 'diantar' && (
            <>
                <p className="address-info">
                    <strong>Alamat Utama:</strong><br/>
                    {userProfile.address || <i>(Belum diisi)</i>}<br/>
                    {userProfile.phone_number || <i>(Belum diisi)</i>}
                </p>
                <div className="optional-address-toggle">
                    <label>
                        <input type="checkbox" checked={useOptionalAddress} onChange={(e) => setUseOptionalAddress(e.target.checked)} />
                        Kirim ke alamat lain?
                    </label>
                </div>
                {useOptionalAddress && (
                    <div className="optional-address-form">
                        <div className="form-group">
                            <label htmlFor="optionalAddress">Alamat Pengiriman Baru</label>
                            <input type="text" id="optionalAddress" name="optionalAddress" placeholder="Masukkan alamat lengkap" value={optionalInfo.optionalAddress} onChange={handleOptionalInfoChange} className="form-input" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="optionalPhone">Nomor Telepon Baru</label>
                            <input type="text" id="optionalPhone" name="optionalPhone" placeholder="Masukkan nomor telepon" value={optionalInfo.optionalPhone} onChange={handleOptionalInfoChange} className="form-input" />
                        </div>
                    </div>
                )}
            </>
        );
    };

    return (
        <>
            <div className="page-container">
                <h2>Keranjang Belanja</h2>
                {cartItems.length === 0 ? (
                    <div className="empty-cart">
                        <p>Keranjang Anda kosong.</p>
                        <Link to="/" className="shop-now-button">Pesan Sekarang</Link>
                    </div>
                ) : (
                    <div className="cart-layout">
                        <div className="cart-items-list">
                           {cartItems.map(item => (
                                <div key={item.Product?.id || item.ProductId} className={`cart-item-card ${!item.Product ? 'disabled' : ''}`}>
                                    <div className="cart-item-image-container">
                                        {item.Product?.Barang?.BarangImages?.[0]?.imageUrl ? 
                                            <img src={`${API_URL}/${item.Product.Barang.BarangImages[0].imageUrl}`} alt={item.Product.Barang?.nama_barang} className="cart-item-image" /> : 
                                            <div className="cart-item-placeholder"><FaImage size={24} /></div>}
                                    </div>
                                    <div className="cart-item-details">
                                        <p>{item.Product?.Barang?.nama_barang || 'Produk Tidak Tersedia'} ({item.Product?.nama_satuan})</p>
                                        <div className="cart-item-quantity">
                                            <p className="cart-item-price">{formatRupiah(item.Product?.price || 0)}</p>
                                            <div className="cart-item-lower">
                                                <button onClick={() => removeFromCart(item.Product.id)} className="remove-button"><FaRegTrashAlt /></button>
                                                {item.Product && <QuantityInput quantity={item.quantity} onChange={(newQuantity) => handleQuantityChange(item, newQuantity)} maxStock={999} />}
                                            </div>
                                        </div>
                                    </div>
                                    {!item.Product && ( <div className="item-unavailable-warning">Produk ini tidak lagi tersedia.</div> )}
                                </div>
                            ))}
                        </div>
                        <div className="cart-summary-card">
                            <h3>Ringkasan Belanja</h3>
                            <div className="summary-row">
                                <span>Total Harga</span>
                                <span>{formatRupiah(total)}</span>
                            </div>
                            <form onSubmit={handleProceedToCheckout}>
                                <hr />
                                <div className="form-group">
                                    <label>Metode Pengiriman</label>
                                    <div className="delivery-method">
                                        <label>
                                            <input type="radio" name="deliveryMethod" value="diantar" checked={deliveryMethod === 'diantar'} onChange={handleDeliveryMethodChange} />
                                            <span className="radio-visual"></span>
                                            <span>Diantar</span>
                                        </label>
                                        <label>
                                            <input type="radio" name="deliveryMethod" value="dijemput" checked={deliveryMethod === 'dijemput'} onChange={handleDeliveryMethodChange} />
                                            <span className="radio-visual"></span>
                                            <span>Dijemput</span>
                                        </label>
                                    </div>
                                </div>
                                
                                {isAuthenticated ? renderAuthenticatedUserForm() : (
                                    <>
                                        <div className="form-group">
                                            <label htmlFor="customerName">Nama Lengkap</label>
                                            <input type="text" id="customerName" name="customerName" placeholder="Masukkan nama Anda" value={guestInfo.customerName} onChange={handleGuestInfoChange} className="form-input" />
                                        </div>
                                        {deliveryMethod === 'diantar' && (
                                            <div className="form-group">
                                                <label htmlFor="customerAddress">Alamat Pengiriman</label>
                                                <input type="text" id="customerAddress" name="customerAddress" placeholder="Masukkan alamat lengkap" value={guestInfo.customerAddress} onChange={handleGuestInfoChange} className="form-input" />
                                            </div>
                                        )}
                                        <div className="form-group">
                                            <label htmlFor="customerPhone">Nomor Telepon</label>
                                            <input type="text" id="customerPhone" name="customerPhone" placeholder="Masukkan nomor telepon" value={guestInfo.customerPhone} onChange={handleGuestInfoChange} className="form-input" />
                                        </div>
                                    </>
                                )}

                                <button type="submit" className="checkout-button" disabled={isLoadingProfile}>
                                    {isLoadingProfile ? 'Memuat...' : 'Lanjut ke Pembayaran'}
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </div>
            <ConfirmationModel isOpen={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)} onConfirm={handleConfirmCheckout} title="Konfirmasi Pesanan">
                Apakah Anda yakin dengan detail pesanan Anda? Produk yang tidak tersedia tidak akan diproses.
            </ConfirmationModel>
        </>
    );
};

export default CartPage;